/**
 * Satış statüsü, `extended_warranty` kaydındaki üç alandan FE'de türetilir:
 * `status`, `activationCode`, `deleted`. BE'de ayrı bir statü kolonu yok.
 *
 *   TASLAK               kayıt açıldı, ödeme henüz alınmadı
 *   ÖDEME GERÇEKLEŞTİ    tahsilat tamamlandı (bayi tahsilatında manuel, online'da webhook)
 *   SÖZLEŞME OLUŞTURULDU poliçe/sözleşme üretildi, müşteriye iletildi
 *   İPTAL EDİLDİ         satış iptal edildi
 *
 * Bayi plan seçip müşteri bilgilerini doldurmadıkça kayıt hiç oluşmaz, yani statü
 * yaşam döngüsü ilk kayıtla başlar (bkz. transactions/newPackage.jsx).
 */
export const SALE_STATUS = {
  DRAFT: "DRAFT",
  // EPS-4971: online ödeme linki gönderilmiş taslaklar. Bunlar hâlâ satış DEĞİL —
  // satış müşteri ödemeyi tamamlayınca oluşuyor.
  PAYMENT_PENDING: "PAYMENT_PENDING",
  PAYMENT_LINK_EXPIRED: "PAYMENT_LINK_EXPIRED",
  // Ödeme alındı, satış kaydı oluşturuluyor. Normalde bu durum listede görünmez
  // (satış oluşunca taslak silinir); görünüyorsa satış oluşturma adımı tamamlanmamıştır
  // ve kesinlikle "Taslak Satış" DEĞİLDİR — bayi o taslaktan satış tamamlamaya
  // çalışmamalı, para zaten tahsil edilmiş.
  PAYMENT_RECEIVED: "PAYMENT_RECEIVED",
  // Ödeme alındı ama satış kaydı oluşturulamadı (analiz F1): operasyon görmeli.
  PAYMENT_COMPLETION_FAILED: "PAYMENT_COMPLETION_FAILED",
  PAID: "PAID",
  CONTRACT_CREATED: "CONTRACT_CREATED",
  CANCELLED: "CANCELLED",
};

/**
 * @param {{status?: string|null, activationCode?: string|null, deleted?: boolean}} sale
 * @returns {keyof typeof SALE_STATUS}
 */
export const getSaleStatus = (sale) => {
  // İptal her adımdan gelebilir, o yüzden ilk kontrol. İki ayrı iptal izi var:
  // soft delete (panel/admin iptali) ve status=CANCELLED — ödeme başarısız olduğunda
  // customer-api yalnızca status'ü CANCELLED'a çekip deleted'a dokunmuyor
  // (ExtendedWarrantyPurchaseService#cancelByPaymentId), bu yüzden ikisine de bakıyoruz.
  if (sale?.deleted === true || sale?.status === "CANCELLED") {
    return SALE_STATUS.CANCELLED;
  }

  // EPS-4971: taslak tarafındaki ödeme durumu, satış statüsünden önce gelir —
  // ödeme linki gönderilmiş bir taslak "Taslak Satış" olarak gösterilmemeli.
  if (sale?.paymentStatus === "LINK_SENT") return SALE_STATUS.PAYMENT_PENDING;
  if (sale?.paymentStatus === "EXPIRED") return SALE_STATUS.PAYMENT_LINK_EXPIRED;
  if (sale?.paymentStatus === "PAID") return SALE_STATUS.PAYMENT_RECEIVED;
  if (sale?.paymentStatus === "COMPLETION_FAILED") {
    return SALE_STATUS.PAYMENT_COMPLETION_FAILED;
  }

  const isCompleted = sale?.status === "COMPLETED";
  const hasActivationCode = Boolean(sale?.activationCode);

  if (isCompleted && hasActivationCode) return SALE_STATUS.CONTRACT_CREATED;
  if (isCompleted) return SALE_STATUS.PAID;

  // status null (legacy kayıtlar) veya PENDING → tahsilat henüz düşmedi.
  return SALE_STATUS.DRAFT;
};

const STATUS_PRESENTATION = {
  [SALE_STATUS.DRAFT]: {
    variant: "yellow",
    labelKey: "extendedWarrantiesPage.status.draft",
    labelFallback: "Taslak Satış",
  },
  [SALE_STATUS.PAYMENT_PENDING]: {
    variant: "blue",
    labelKey: "extendedWarrantiesPage.status.paymentPending",
    labelFallback: "Ödeme Bekleniyor",
  },
  [SALE_STATUS.PAYMENT_LINK_EXPIRED]: {
    variant: "yellow",
    labelKey: "extendedWarrantiesPage.status.paymentLinkExpired",
    labelFallback: "Link Süresi Doldu",
  },
  [SALE_STATUS.PAYMENT_RECEIVED]: {
    variant: "blue",
    labelKey: "extendedWarrantiesPage.status.paymentReceived",
    labelFallback: "Ödeme Alındı",
  },
  [SALE_STATUS.PAYMENT_COMPLETION_FAILED]: {
    variant: "red",
    labelKey: "extendedWarrantiesPage.status.paymentCompletionFailed",
    labelFallback: "Ödeme Alındı / Satış Oluşmadı",
  },
  [SALE_STATUS.PAID]: {
    variant: "blue",
    labelKey: "extendedWarrantiesPage.status.paid",
    labelFallback: "Ödeme Gerçekleşti",
  },
  [SALE_STATUS.CONTRACT_CREATED]: {
    variant: "green",
    labelKey: "extendedWarrantiesPage.status.contractCreated",
    labelFallback: "Sözleşme Oluşturuldu",
  },
  [SALE_STATUS.CANCELLED]: {
    variant: "red",
    labelKey: "extendedWarrantiesPage.status.cancelled",
    labelFallback: "İptal Edildi",
  },
};

export const getSaleStatusPresentation = (sale) =>
  STATUS_PRESENTATION[getSaleStatus(sale)];

/** EPS-5115: statü filtresi seçeneklerinin etiketi listedeki rozetle aynı yerden gelir. */
export const getSaleStatusPresentationByKey = (status) =>
  STATUS_PRESENTATION[status];

export const ALL_SALES_FILTER = "ALL_SALES";
export const ACTIVE_SALES_FILTER = "ACTIVE";

/**
 * EPS-5115: Satışlar listesinin statü filtresi — seçenekler (bu sırayla) ve her birinin
 * iki kaynaktaki karşılığı. Statüler iki ayrı yerden türetildiği için filtre de ikiye
 * bölünüyor:
 *
 *   drafts → bff seller_sale_draft ucu (`status`): taslak + ödeme linki statüleri
 *   sales  → customer-api extended_warranty ucu: satış statüleri
 *
 * Tanımsız kaynak hiç sorgulanmaz. Her iki uçtaki koşul yukarıdaki `getSaleStatus`
 * türetmesinin birebir SQL karşılığı — bir statü seçilince listede başka etiket
 * görünmemeli. "Taslak Satış" iki kaynakta da var: ödeme linki olmayan taslaklar ve
 * ödemesi beklenen (status PENDING) satın alma kayıtları.
 */
export const SALE_STATUS_FILTERS = {
  [ALL_SALES_FILTER]: { drafts: {}, sales: { includeCancelled: true } },
  [ACTIVE_SALES_FILTER]: { sales: { saleStatus: "ACTIVE" } },
  [SALE_STATUS.DRAFT]: {
    drafts: { status: SALE_STATUS.DRAFT },
    sales: { saleStatus: SALE_STATUS.DRAFT },
  },
  [SALE_STATUS.PAYMENT_PENDING]: {
    drafts: { status: SALE_STATUS.PAYMENT_PENDING },
  },
  [SALE_STATUS.PAYMENT_LINK_EXPIRED]: {
    drafts: { status: SALE_STATUS.PAYMENT_LINK_EXPIRED },
  },
  [SALE_STATUS.PAID]: { sales: { saleStatus: SALE_STATUS.PAID } },
  [SALE_STATUS.CONTRACT_CREATED]: {
    sales: { saleStatus: SALE_STATUS.CONTRACT_CREATED },
  },
  [SALE_STATUS.CANCELLED]: { sales: { saleStatus: SALE_STATUS.CANCELLED } },
  // Listede seyrek görünen ama sistemde tanımlı iki statü (ödeme alındı, satış
  // oluşturma adımı bitmedi / başarısız).
  [SALE_STATUS.PAYMENT_RECEIVED]: {
    drafts: { status: SALE_STATUS.PAYMENT_RECEIVED },
  },
  [SALE_STATUS.PAYMENT_COMPLETION_FAILED]: {
    drafts: { status: SALE_STATUS.PAYMENT_COMPLETION_FAILED },
  },
};

export const isDeletableDraft = (row) =>
  Boolean(row?.isDraft) &&
  (row.paymentStatus == null || row.paymentStatus === "EXPIRED");
